import { Building2, Package, Users, Check, ChevronRight } from 'lucide-react';
import type { StepDefinition, StepKey } from '../config/onboarding.types';

const ICONS: Record<StepKey, typeof Building2> = {
  company: Building2,
  product: Package,
  customer: Users,
};

interface StepRowProps {
  step: StepDefinition;
  isDone: boolean;
  isOpen: boolean;
  isLast: boolean;
  /** Versión compacta, sin descripción ni acordeón (empresas 2 en adelante, si se abre desde Ayuda). */
  compact: boolean;
  /** Abre o cierra el acordeón. */
  onToggle: () => void;
  /** Navega a la pantalla del paso. */
  onGo: () => void;
}

/**
 * Un paso del checklist. Hecho: título corto, check verde y riel verde (sin
 * etiqueta "Listo": el check ya lo dice). Pendiente: título completo y, si
 * está abierto, descripción y acción.
 */
export function StepRow({
  step,
  isDone,
  isOpen,
  isLast,
  compact,
  onToggle,
  onGo,
}: StepRowProps) {
  const Icon = ICONS[step.key];
  const iconBox = compact ? 'h-6 w-6' : 'h-7 w-7';
  const railLeft = compact ? 'left-[17px] top-[33px]' : 'left-[19px] top-[39px]';

  return (
    <div className="relative">
      {/* riel de conexión: se tiñe de verde al completarse */}
      {!isLast && (
        <span
          aria-hidden
          className={[
            'absolute bottom-px w-0.5 rounded-sm transition-colors duration-500',
            railLeft,
            isDone ? 'bg-[var(--success)]' : 'bg-[var(--border-muted)]',
          ].join(' ')}
        />
      )}

      <button
        type="button"
        disabled={isDone}
        aria-expanded={compact || isDone ? undefined : isOpen}
        // En la versión compacta la fila navega directo: el usuario ya sabe qué es cada paso.
        onClick={compact ? onGo : onToggle}
        className={[
          'flex w-full items-center gap-[11px] rounded-[6px] px-1.5 text-left transition-colors',
          compact ? 'py-2' : 'py-[9px]',
          isDone ? 'cursor-default' : 'hover:bg-surface-1',
        ].join(' ')}
      >
        <span
          className={[
            iconBox,
            'z-10 flex shrink-0 items-center justify-center rounded-full border transition-all duration-300',
            isDone
              ? 'border-[var(--success)] bg-[var(--success)] text-inverse'
              : isOpen
                ? 'border-brand bg-brand-light text-brand shadow-[0_0_0_3px_rgba(22,98,197,0.10)]'
                : 'border-muted bg-surface-1 text-subtle',
          ].join(' ')}
        >
          {isDone ? (
            <Check className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} strokeWidth={3.2} />
          ) : (
            <Icon className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} strokeWidth={1.9} />
          )}
        </span>

        <span className="min-w-0 flex-1">
          <span
            className={[
              'block leading-snug transition-colors',
              isDone || compact ? 'text-ui-sm' : 'text-body-sm',
              isDone
                ? 'font-normal text-muted'
                : isOpen
                  ? 'font-semibold text-primary'
                  : 'font-medium text-tertiary',
            ].join(' ')}
          >
            {isDone || compact ? step.shortTitle : step.title}
          </span>
        </span>

        {!isDone && (
          <ChevronRight
            className={[
              'h-[15px] w-[15px] shrink-0 transition-transform duration-300',
              isOpen ? 'rotate-90 text-brand' : 'text-subtle',
            ].join(' ')}
            strokeWidth={2.4}
          />
        )}
      </button>

      {/* Acordeón. Solo se anima esta zona: el panel no se mueve. */}
      {!compact && (
        <div
          className={[
            'overflow-hidden pl-[45px] transition-[max-height,opacity,padding-bottom] duration-300 ease-[cubic-bezier(.32,.72,0,1)]',
            isOpen ? 'max-h-48 pb-2.5 opacity-100' : 'max-h-0 pb-0 opacity-0',
          ].join(' ')}
        >
          <p className="text-caption leading-relaxed text-tertiary">{step.description}</p>
          <button
            type="button"
            onClick={onGo}
            tabIndex={isOpen ? 0 : -1}
            className="-ml-1.5 mt-1.5 inline-flex items-center gap-1 rounded p-1.5 text-ui-sm font-semibold text-brand transition-colors hover:text-brand-hover"
          >
            <span className="border-b border-transparent pb-px transition-colors [button:hover>&]:border-current">
              {step.action}
            </span>
            <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 [button:hover>&]:translate-x-0.5" strokeWidth={2.4} />
          </button>
        </div>
      )}
    </div>
  );
}
